import {
  RestfulSiteAnnouncementWebService,
  SiteAnnouncementWebService
} from 'ws/SiteAnnouncementWebService';
import {
  SiteAnnouncement,
  SiteAnnouncementFormData,
  SiteAnnouncementListDTO
} from './SiteAnnouncement';
import {
  CloudStorageManager,
  DefaultCloudStorageManager
} from 'core/cloudStorage/CloudStorageManager';
import { omit } from 'lodash';

export interface SiteAnnouncementManager {
  getSiteAnnouncement: (announcementId: number) => Promise<SiteAnnouncement>;
  getSiteAnnouncements: () => Promise<SiteAnnouncementListDTO[]>;
  createSiteAnnouncement: (
    announcement: SiteAnnouncementFormData
  ) => Promise<void>;
  updateSiteAnnouncement: (
    id: number,
    announcement: SiteAnnouncementFormData
  ) => Promise<void>;
  getTopAnnouncement: (
    agencyId: number
  ) => Promise<SiteAnnouncement | undefined>;
  activateAnnouncements: (ids: number[]) => Promise<void>;
  deactivateAnnouncements: (ids: number[]) => Promise<void>;
  deleteAnnouncements: (ids: number[]) => Promise<void>;
}

export class DefaultSiteAnnouncementManager implements SiteAnnouncementManager {
  constructor(
    private webService: SiteAnnouncementWebService = new RestfulSiteAnnouncementWebService(),
    private cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager()
  ) {}

  async getSiteAnnouncement(announcementId: number): Promise<SiteAnnouncement> {
    return this.webService.getSiteAnnouncement(announcementId);
  }

  async getSiteAnnouncements(): Promise<SiteAnnouncementListDTO[]> {
    return this.webService.getSiteAnnouncements();
  }

  async createSiteAnnouncement(
    announcement: SiteAnnouncementFormData
  ): Promise<void> {
    return this.webService.createSiteAnnouncement(
      await this.toCreateDTO(announcement)
    );
  }

  async updateSiteAnnouncement(
    id: number,
    announcement: SiteAnnouncementFormData
  ): Promise<void> {
    return this.webService.updateSiteAnnouncement({
      ...(await this.toCreateDTO(announcement)),
      shouldResend: announcement.shouldResend,
      id
    });
  }

  // Flatten the file-input `image`/`attachments` values ({ file, url }) down to
  // the persisted `imageUrl` / `attachmentUrls` the API expects. Freshly picked
  // files are uploaded to cloud storage first to obtain persisted URLs;
  // otherwise the existing urls (if any) are kept.
  private async toCreateDTO(announcement: SiteAnnouncementFormData) {
    const { image, attachments, ...rest } = omit(
      announcement,
      'chooseAgencyStrategy',
      'shouldResend'
    );
    const imageUrl = image?.file
      ? await this.cloudStorageManager.uploadFileToCloud(image.file)
      : image?.url;
    const attachmentUrls = await this.uploadAttachments(attachments);
    return {
      ...rest,
      imageUrl,
      attachmentUrls
    };
  }

  private async uploadAttachments(
    attachments: SiteAnnouncementFormData['attachments']
  ): Promise<string[]> {
    if (!attachments || attachments.length === 0) {
      return [];
    }
    const urls = await Promise.all(
      attachments.map((attachment) =>
        attachment.file
          ? this.cloudStorageManager.uploadFileToCloud(
              attachment.file,
              undefined,
              undefined,
              true
            )
          : attachment.url
      )
    );
    return urls.filter((url): url is string => !!url);
  }

  async getTopAnnouncement(
    agencyId: number
  ): Promise<SiteAnnouncement | undefined> {
    return this.webService.getTopAnnouncement(agencyId);
  }

  async activateAnnouncements(ids: number[]): Promise<void> {
    return this.webService.activateAnnouncements(ids);
  }

  async deactivateAnnouncements(ids: number[]): Promise<void> {
    return this.webService.deactivateAnnouncements(ids);
  }

  async deleteAnnouncements(ids: number[]): Promise<void> {
    return this.webService.deleteAnnouncements(ids);
  }
}
